'use client';

/**
 * SmsOptInDisclosure — TCPA / A2P SMS + voice opt-in disclosure with two
 * SEPARATE explicit, unchecked consent checkboxes:
 *
 *   1. SMS consent      — transactional SMS only (status, scheduling, docs)
 *   2. Voice consent    — autodialed / prerecorded-voice phone calls
 *
 * Required for U.S. carrier (TCR) approval of A2P SMS campaigns and for
 * TCPA "prior express written consent" on autodialed/prerecorded voice.
 * Twilio explicitly rejects bundled SMS+voice checkboxes — the two must
 * be separate so the consumer's affirmative action is unambiguous.
 *
 * Implied-consent language ("By submitting this form, you agree...") is a
 * rejection trigger — the user MUST actively check each box to opt in.
 *
 * Portal-agnostic: pass `companyName` explicitly from the calling portal's
 * branding/tenant context. Returns null when no companyName is supplied —
 * better to show no disclosure than to ship an unsigned one.
 *
 * Submission gating: both checkboxes are OPTIONAL by default
 * (`smsConsentRequired = false`). The platform stopped enforcing SMS
 * consent at submit time as a product call — manually-dialed live-agent
 * calls and one-off transactional texts triggered by user actions don't
 * require prior express written consent under TCPA. Tenants whose
 * downstream comms include autodialed / prerecorded SMS reminders should
 * opt back into the strict gate by passing
 * `{ smsConsentRequired: true }` to `useSmsOptInConsent` — the bound flag
 * flows through into this component via the spread `consent.bind` and
 * re-enables the asterisk + native `required` attribute + validation
 * error frame. The voice checkbox stays optional regardless (manually-
 * dialed calls don't need it).
 *
 * Promotional / marketing SMS is NOT offered on this surface. Tenants
 * who want marketing SMS must register a separate marketing campaign
 * with their SMS provider and surface a dedicated promotional opt-in.
 * Bundling marketing into a transactional opt-in is the most common
 * carrier-rejection cause.
 *
 * The two boolean values flow into the lead-submission payload as
 * `smsConsentSms` and `smsConsentVoice` so downstream messaging can
 * honor them.
 *
 * Pair with `useSmsOptInConsent` for the standard state + payload binding.
 *
 * @see https://help.close.com/docs/a2p-sms-registration-rejected
 */

import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useI18n } from '../../hooks/useI18n';
import { GlassCheckbox } from '../GlassCheckbox';

export interface SmsOptInDisclosureProps {
  /** Sender name shown throughout the disclosure. Returns null when missing. */
  companyName?: string;

  /** Terms of Service path/URL. Defaults to /terms-of-service. */
  termsHref?: string;

  /** Privacy Policy path/URL. Defaults to /privacy-policy. */
  privacyHref?: string;

  /** Whether the user has opted in to transactional SMS. */
  smsConsent: boolean;

  /** Called when the SMS checkbox toggles. */
  onSmsConsentChange: (checked: boolean) => void;

  /** Whether the user has opted in to autodialed / prerecorded voice calls. */
  voiceConsent: boolean;

  /** Called when the voice checkbox toggles. */
  onVoiceConsentChange: (checked: boolean) => void;

  /**
   * When `true`, mark the SMS consent checkbox as required — the label
   * gets an asterisk and `aria-required`, and the checkbox gets the HTML
   * `required` attribute. Defaults to `false` (the platform-wide default).
   * Consumer forms wire this from
   * `useSmsOptInConsent().bind.smsConsentRequired` so a single option in
   * the hook drives both presentation and the `validate()` gate. Pass
   * explicit `true` for tenants whose downstream comms include
   * autodialed / prerecorded SMS reminders (TCPA "prior express written
   * consent" rule).
   */
  smsConsentRequired?: boolean;

  /**
   * Set to `true` to draw the error state on the SMS checkbox (red
   * border + "Required" helper text). Consumer forms typically wire this
   * from `useSmsOptInConsent().bind.showValidationError`, which is
   * flipped on by `validate()` and cleared automatically when the user
   * checks the box.
   */
  showValidationError?: boolean;

  /** Optional sx overrides for the wrapper (e.g. spacing relative to surrounding form). */
  sx?: SxProps<Theme>;
}

export function SmsOptInDisclosure({
  companyName,
  termsHref = '/terms-of-service',
  privacyHref = '/privacy-policy',
  smsConsent,
  onSmsConsentChange,
  voiceConsent,
  onVoiceConsentChange,
  smsConsentRequired = false,
  showValidationError = false,
  sx,
}: SmsOptInDisclosureProps) {
  const { t } = useI18n();

  if (!companyName) return null;

  // Treat hrefs starting with `/` as in-app routes; only externals open a new tab.
  const isExternal = (href: string) => !href.startsWith('/');

  return (
    <Box
      data-testid="sms-opt-in-disclosure"
      sx={[
        {
          mt: 2,
          px: 2,
          py: 1.5,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.6, mb: 1.5 }}>
        {t('website.smsOptInDisclosure.body', { companyName })}{' '}
        <Link
          href={termsHref}
          underline="hover"
          {...(isExternal(termsHref) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {t('website.smsOptInDisclosure.terms')}
        </Link>{' '}
        {t('website.smsOptInDisclosure.and')}{' '}
        <Link
          href={privacyHref}
          underline="hover"
          {...(isExternal(privacyHref) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {t('website.smsOptInDisclosure.privacy')}
        </Link>
        .
      </Typography>

      <Stack spacing={1}>
        <Box
          sx={{
            // Draw the error frame around the SMS row when submission was
            // attempted without consent. Red border + soft tint mirror MUI's
            // standard error-input look so it's familiar without pulling in a
            // full FormControl wrapper.
            ...(smsConsentRequired && showValidationError && !smsConsent
              ? {
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'error.main',
                  bgcolor: 'error.light',
                  px: 1,
                  py: 0.5,
                  mx: -1,
                }
              : {}),
          }}
        >
          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0, '& .MuiFormControlLabel-label': { mt: 0.25 } }}
            control={
              <GlassCheckbox
                data-testid="sms-opt-in-sms"
                checked={smsConsent}
                onChange={(e) => onSmsConsentChange(e.target.checked)}
                // ARIA spec puts `aria-required` on the form control itself
                // (the input), not on a wrapping label. MUI 9 dropped the
                // legacy `inputProps` shape in favor of `slotProps.input`,
                // which forwards attributes onto the underlying <input>.
                slotProps={{
                  input: {
                    'aria-required': smsConsentRequired,
                    required: smsConsentRequired,
                  },
                }}
                sx={{ pt: 0, pl: 0 }}
              />
            }
            label={
              <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.5 }}>
                {t('website.smsOptInDisclosure.consentSms', { companyName })}
                {smsConsentRequired && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: 'error.main', ml: 0.5, fontWeight: 600 }}
                    aria-hidden="true"
                  >
                    *
                  </Typography>
                )}
              </Typography>
            }
          />
          {smsConsentRequired && showValidationError && !smsConsent && (
            <Typography
              variant="caption"
              role="alert"
              sx={{ color: 'error.main', display: 'block', mt: 0.5, ml: 4, fontWeight: 500 }}
            >
              {t('website.smsOptInDisclosure.requiredError', 'Required to submit — please check this box to continue.')}
            </Typography>
          )}
        </Box>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', m: 0, '& .MuiFormControlLabel-label': { mt: 0.25 } }}
          control={
            <GlassCheckbox
              data-testid="sms-opt-in-voice"
              checked={voiceConsent}
              onChange={(e) => onVoiceConsentChange(e.target.checked)}
              sx={{ pt: 0, pl: 0 }}
            />
          }
          label={
            <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.5 }}>
              {t('website.smsOptInDisclosure.consentVoice', { companyName })}
            </Typography>
          }
        />
      </Stack>
    </Box>
  );
}

export default SmsOptInDisclosure;
